import type { ProductFamily, PublicProductType } from './productFamily'
export type ProductPresentation = {
  imageScale: number
  imageOffsetX: number
  imageOffsetY: number
  hoverBackground: string
}

export type ProductFraming = {
  imageScale: number
  imageOffsetX: number
  imageOffsetY: number
}

export type ProductImageFraming = {
  focalX: number
  focalY: number
  zoom: number
}

export const PRODUCT_IMAGE_FRAMING_LIMITS = {
  minFocal: 0,
  maxFocal: 1,
  minZoom: 1,
  maxZoom: 2.5,
} as const

export const DEFAULT_PRODUCT_IMAGE_FRAMING: Readonly<ProductImageFraming> = {
  focalX: 0.5,
  focalY: 0.5,
  zoom: 1,
}

export class ProductImageFramingValidationError extends Error {
  readonly field: keyof ProductImageFraming

  constructor(field: keyof ProductImageFraming, message: string) {
    super(message)
    this.name = 'ProductImageFramingValidationError'
    this.field = field
  }
}

function finiteFramingNumber(value: unknown, field: keyof ProductImageFraming, min: number, max: number) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    throw new ProductImageFramingValidationError(field, `${field} is outside the allowed range.`)
  }
  return value
}

export function validateProductImageFraming(value: unknown): ProductImageFraming {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ProductImageFramingValidationError('zoom', 'Product image framing must be an object.')
  }
  const candidate = value as Record<string, unknown>
  return {
    focalX: finiteFramingNumber(
      candidate.focalX,
      'focalX',
      PRODUCT_IMAGE_FRAMING_LIMITS.minFocal,
      PRODUCT_IMAGE_FRAMING_LIMITS.maxFocal,
    ),
    focalY: finiteFramingNumber(
      candidate.focalY,
      'focalY',
      PRODUCT_IMAGE_FRAMING_LIMITS.minFocal,
      PRODUCT_IMAGE_FRAMING_LIMITS.maxFocal,
    ),
    zoom: finiteFramingNumber(
      candidate.zoom,
      'zoom',
      PRODUCT_IMAGE_FRAMING_LIMITS.minZoom,
      PRODUCT_IMAGE_FRAMING_LIMITS.maxZoom,
    ),
  }
}

export function normalizeStoredProductImageFraming(
  value: unknown,
  fallback: ProductImageFraming = DEFAULT_PRODUCT_IMAGE_FRAMING,
): ProductImageFraming {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { ...fallback }
  const candidate = value as Record<string, unknown>
  const focalX = typeof candidate.focalX === 'number'
    && Number.isFinite(candidate.focalX)
    && candidate.focalX >= PRODUCT_IMAGE_FRAMING_LIMITS.minFocal
    && candidate.focalX <= PRODUCT_IMAGE_FRAMING_LIMITS.maxFocal
    ? candidate.focalX
    : fallback.focalX
  const focalY = typeof candidate.focalY === 'number'
    && Number.isFinite(candidate.focalY)
    && candidate.focalY >= PRODUCT_IMAGE_FRAMING_LIMITS.minFocal
    && candidate.focalY <= PRODUCT_IMAGE_FRAMING_LIMITS.maxFocal
    ? candidate.focalY
    : fallback.focalY
  const zoom = typeof candidate.zoom === 'number'
    && Number.isFinite(candidate.zoom)
    && candidate.zoom >= PRODUCT_IMAGE_FRAMING_LIMITS.minZoom
    && candidate.zoom <= PRODUCT_IMAGE_FRAMING_LIMITS.maxZoom
    ? candidate.zoom
    : fallback.zoom
  return { focalX, focalY, zoom }
}

export function imageFramingEquals(left: ProductImageFraming, right: ProductImageFraming) {
  return left.focalX === right.focalX
    && left.focalY === right.focalY
    && left.zoom === right.zoom
}

export const PRODUCT_PRESENTATION_LIMITS = {
  minScale: 0.5,
  maxScale: 2.5,
  minOffset: -50,
  maxOffset: 50,
} as const

export const ORDER_PRODUCT_PRESENTATION_LIMITS = {
  ...PRODUCT_PRESENTATION_LIMITS,
} as const

export const PRODUCT_HOVER_DEFAULTS: Readonly<Record<string, string>> = {
  vanilla: '#F4DFC0',
  'bcs-chocolate': '#F4DFC0',
  blueberry: '#D9D3EC',
  'bcs-vanilla': '#D9D3EC',
  chocolate: '#D4B3A2',
  'bcs-pineapple': '#D4B3A2',
  'toasted-coconut': '#EBD6B9',
  'bcs-cookie-dough': '#EBD6B9',
  biscoff: '#E1C29E',
  'msc-og-chocolate': '#E1C29E',
  bueno: '#E5CBB9',
  'msc-raspberry-white-chocolate-cheesecake': '#E5CBB9',
  'banana-caramel-pb': '#F0D59E',
  'msc-caramel': '#F0D59E',
  'og-chocolate': '#CFB2A6',
  'brownie-ny-double-choc': '#CFB2A6',
}

export class ProductPresentationValidationError extends Error {
  readonly field: keyof ProductPresentation

  constructor(field: keyof ProductPresentation, message: string) {
    super(message)
    this.name = 'ProductPresentationValidationError'
    this.field = field
  }
}

export function approvedHoverBackground(productId: string) {
  return PRODUCT_HOVER_DEFAULTS[productId] ?? null
}

type PresentationProductType = ProductFamily | PublicProductType

export function defaultProductPresentation(
  productId: string,
  productType: PresentationProductType,
): ProductPresentation {
  const hoverBackground = approvedHoverBackground(productId) ?? '#F7EDE2'
  switch (productType) {
    case 'Massive Stuffed Cookie':
    case 'Stuffed Cookie':
      return { imageScale: 1.22, imageOffsetX: 0, imageOffsetY: 0, hoverBackground }
    case 'Brownie':
      return { imageScale: 1, imageOffsetX: 0, imageOffsetY: 0, hoverBackground }
    case 'Brownie Cookie Sandwich':
      return { imageScale: 1, imageOffsetX: 0, imageOffsetY: 0, hoverBackground }
  }
}

export function defaultOrderProductFraming(
  productType: PresentationProductType,
): ProductFraming {
  switch (productType) {
    case 'Massive Stuffed Cookie':
    case 'Stuffed Cookie':
      return { imageScale: 1.82, imageOffsetX: 0, imageOffsetY: -12 }
    case 'Brownie':
      return { imageScale: 1, imageOffsetX: 0, imageOffsetY: 0 }
    case 'Brownie Cookie Sandwich':
      return { imageScale: 1, imageOffsetX: 0, imageOffsetY: 0 }
  }
}

export function normalizeHexColor(value: unknown) {
  if (typeof value !== 'string' || !/^#[0-9A-Fa-f]{6}$/.test(value)) return null
  return value.toUpperCase()
}

function finiteNumber(value: unknown, field: keyof ProductPresentation, min: number, max: number) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    throw new ProductPresentationValidationError(field, `${field} is outside the allowed range.`)
  }
  return value
}

export function validateProductFraming(value: unknown): ProductFraming {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ProductPresentationValidationError('imageScale', 'Product framing must be an object.')
  }
  const candidate = value as Record<string, unknown>
  return {
    imageScale: finiteNumber(
      candidate.imageScale,
      'imageScale',
      ORDER_PRODUCT_PRESENTATION_LIMITS.minScale,
      ORDER_PRODUCT_PRESENTATION_LIMITS.maxScale,
    ),
    imageOffsetX: finiteNumber(
      candidate.imageOffsetX,
      'imageOffsetX',
      ORDER_PRODUCT_PRESENTATION_LIMITS.minOffset,
      ORDER_PRODUCT_PRESENTATION_LIMITS.maxOffset,
    ),
    imageOffsetY: finiteNumber(
      candidate.imageOffsetY,
      'imageOffsetY',
      ORDER_PRODUCT_PRESENTATION_LIMITS.minOffset,
      ORDER_PRODUCT_PRESENTATION_LIMITS.maxOffset,
    ),
  }
}

export function normalizeStoredProductFraming(
  value: Partial<Record<keyof ProductFraming, unknown>>,
  fallback: ProductFraming,
): ProductFraming {
  const scale = typeof value.imageScale === 'number'
    && Number.isFinite(value.imageScale)
    && value.imageScale >= ORDER_PRODUCT_PRESENTATION_LIMITS.minScale
    && value.imageScale <= ORDER_PRODUCT_PRESENTATION_LIMITS.maxScale
    ? value.imageScale
    : fallback.imageScale
  const offsetX = typeof value.imageOffsetX === 'number'
    && Number.isFinite(value.imageOffsetX)
    && value.imageOffsetX >= ORDER_PRODUCT_PRESENTATION_LIMITS.minOffset
    && value.imageOffsetX <= ORDER_PRODUCT_PRESENTATION_LIMITS.maxOffset
    ? value.imageOffsetX
    : fallback.imageOffsetX
  const offsetY = typeof value.imageOffsetY === 'number'
    && Number.isFinite(value.imageOffsetY)
    && value.imageOffsetY >= ORDER_PRODUCT_PRESENTATION_LIMITS.minOffset
    && value.imageOffsetY <= ORDER_PRODUCT_PRESENTATION_LIMITS.maxOffset
    ? value.imageOffsetY
    : fallback.imageOffsetY
  return { imageScale: scale, imageOffsetX: offsetX, imageOffsetY: offsetY }
}

export function framingEquals(left: ProductFraming, right: ProductFraming) {
  return left.imageScale === right.imageScale
    && left.imageOffsetX === right.imageOffsetX
    && left.imageOffsetY === right.imageOffsetY
}

export function validateProductPresentation(value: unknown): ProductPresentation {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ProductPresentationValidationError('imageScale', 'Product presentation must be an object.')
  }
  const candidate = value as Record<string, unknown>
  const hoverBackground = normalizeHexColor(candidate.hoverBackground)
  if (!hoverBackground) {
    throw new ProductPresentationValidationError('hoverBackground', 'Hover background must use #RRGGBB.')
  }
  return {
    imageScale: finiteNumber(
      candidate.imageScale,
      'imageScale',
      PRODUCT_PRESENTATION_LIMITS.minScale,
      PRODUCT_PRESENTATION_LIMITS.maxScale,
    ),
    imageOffsetX: finiteNumber(
      candidate.imageOffsetX,
      'imageOffsetX',
      PRODUCT_PRESENTATION_LIMITS.minOffset,
      PRODUCT_PRESENTATION_LIMITS.maxOffset,
    ),
    imageOffsetY: finiteNumber(
      candidate.imageOffsetY,
      'imageOffsetY',
      PRODUCT_PRESENTATION_LIMITS.minOffset,
      PRODUCT_PRESENTATION_LIMITS.maxOffset,
    ),
    hoverBackground,
  }
}

export function normalizeStoredProductPresentation(
  value: Partial<Record<keyof ProductPresentation, unknown>>,
  fallback: ProductPresentation,
): ProductPresentation {
  const scale = typeof value.imageScale === 'number'
    && Number.isFinite(value.imageScale)
    && value.imageScale >= PRODUCT_PRESENTATION_LIMITS.minScale
    && value.imageScale <= PRODUCT_PRESENTATION_LIMITS.maxScale
    ? value.imageScale
    : fallback.imageScale
  const offsetX = typeof value.imageOffsetX === 'number'
    && Number.isFinite(value.imageOffsetX)
    && value.imageOffsetX >= PRODUCT_PRESENTATION_LIMITS.minOffset
    && value.imageOffsetX <= PRODUCT_PRESENTATION_LIMITS.maxOffset
    ? value.imageOffsetX
    : fallback.imageOffsetX
  const offsetY = typeof value.imageOffsetY === 'number'
    && Number.isFinite(value.imageOffsetY)
    && value.imageOffsetY >= PRODUCT_PRESENTATION_LIMITS.minOffset
    && value.imageOffsetY <= PRODUCT_PRESENTATION_LIMITS.maxOffset
    ? value.imageOffsetY
    : fallback.imageOffsetY
  return {
    imageScale: scale,
    imageOffsetX: offsetX,
    imageOffsetY: offsetY,
    hoverBackground: normalizeHexColor(value.hoverBackground) ?? fallback.hoverBackground,
  }
}

export function presentationEquals(left: ProductPresentation, right: ProductPresentation) {
  return left.imageScale === right.imageScale
    && left.imageOffsetX === right.imageOffsetX
    && left.imageOffsetY === right.imageOffsetY
    && left.hoverBackground === right.hoverBackground
}
